import React, { useEffect, useMemo, useState } from "react";
import { Form, Input, Select } from "antd";
import { useDispatch, useSelector } from "react-redux";
import { AiOutlineReload } from "react-icons/ai";
import { Modal, OverlayTrigger, Tooltip } from "react-bootstrap";
import { useLocation } from "react-router-dom";
import { MdDelete } from "react-icons/md";
import axios from "axios";
import swal from "sweetalert";

const { Option } = Select;

const STATUS_OPTIONS = [
  { value: "scheduled", label: "scheduled" },
  { value: "live", label: "live" },
  { value: "ended", label: "ended" },
  { value: "finalized", label: "finalized" },
];
const ALLOWED_STATUS_TRANSITIONS = {
  scheduled: ["scheduled", "live", "ended"],
  live: ["live", "ended"],
  ended: ["ended"],
  finalized: ["finalized"],
};

const CompetitionV2Admin = () => {
  const { state } = useLocation();
  const presetCompetitionId = state?.competitionId || "";
  const dispatch = useDispatch();
  const [manageForm] = Form.useForm();
  const [finalizeForm] = Form.useForm();

  const [loading, setLoading] = useState(false);
  const [selectedCompetitionId, setSelectedCompetitionId] = useState("");
  const [v2Competitions, setV2Competitions] = useState([]);
  const [selectedCompetitionDetail, setSelectedCompetitionDetail] = useState(null);
  const [competitionSearch, setCompetitionSearch] = useState("");
  const [showLeaderboardModal, setShowLeaderboardModal] = useState(false);
  const [leaderboardRows, setLeaderboardRows] = useState([]);
  const [leaderboardLoading, setLeaderboardLoading] = useState(false);
  const [showWinnersModal, setShowWinnersModal] = useState(false);
  const [winnersRows, setWinnersRows] = useState([]);
  const [winnersLoading, setWinnersLoading] = useState(false);
  const [winnersPublished, setWinnersPublished] = useState(false);
  const [addWinnerSubscriberId, setAddWinnerSubscriberId] = useState("");
  const [showFinalizeModal, setShowFinalizeModal] = useState(false);

  const selectedCompetition = useMemo(
    () =>
      selectedCompetitionDetail ||
      v2Competitions.find((item) => item._id === selectedCompetitionId) ||
      null,
    [selectedCompetitionDetail, selectedCompetitionId, v2Competitions]
  );

  const competitionOptions = useMemo(
    () => {
      const base = v2Competitions.map((item) => ({
        value: item._id,
        label: `${item.NameEn || "N/A"} (${item.CompetitionCategory}) [${item.Status}]`,
      }));
      if (
        selectedCompetitionDetail?._id &&
        !base.some((item) => item.value === selectedCompetitionDetail._id)
      ) {
        base.unshift({
          value: selectedCompetitionDetail._id,
          label: `${selectedCompetitionDetail.NameEn || "N/A"} (${selectedCompetitionDetail.CompetitionCategory}) [${selectedCompetitionDetail.Status}]`,
        });
      }
      return base;
    },
    [v2Competitions, selectedCompetitionDetail]
  );
  const availableStatusOptions = useMemo(() => {
    const currentStatus = selectedCompetition?.Status;
    const allowed = currentStatus
      ? ALLOWED_STATUS_TRANSITIONS[currentStatus] || [currentStatus]
      : STATUS_OPTIONS.map((s) => s.value);
    return STATUS_OPTIONS.filter((opt) => allowed.includes(opt.value));
  }, [selectedCompetition?.Status]);
  const hideFinalizeButton = useMemo(() => {
    if (!selectedCompetition) return false;
    if (selectedCompetition.Status === "finalized") return true;
    const winnersDraftReady =
      selectedCompetition.Status === "ended" &&
      Number(selectedCompetition.WinnerCount || 0) > 0 &&
      !selectedCompetition.WinnersPublishedAtUtc;
    return winnersDraftReady;
  }, [selectedCompetition]);
  const winnersPrizeTotal = useMemo(
    () => (winnersRows || []).reduce((sum, row) => sum + Number(row?.PrizeAmount || 0), 0),
    [winnersRows]
  );
  const addWinnerOptions = useMemo(() => {
    const existing = new Set((winnersRows || []).map((row) => row?.SubscriberID).filter(Boolean));
    return (leaderboardRows || [])
      .map((row, index) => ({
        row,
        rank: index + 1,
      }))
      .filter(({ row }) => row?.SubscriberID && !existing.has(row.SubscriberID))
      .map(({ row, rank }) => {
        const name =
          `${row?.CompetitionLeaderboardSubscriberDataV2?.FirstName || ""} ${row?.CompetitionLeaderboardSubscriberDataV2?.LastName || ""}`.trim() ||
          row.SubscriberID;
        const points = Number(row?.TotalPoint || 0);
        return {
          value: row.SubscriberID,
          label: `#${rank} - ${name} (${points} pts)`,
        };
      });
  }, [leaderboardRows, winnersRows]);
  const fetchCompetitions = async (keyword = "") => {
    const params = new URLSearchParams();
    params.set("includeRaces", "false");
    params.set("page", "1");
    params.set("size", "10");
    if (keyword.trim()) {
      params.set("keyword", keyword.trim());
    }
    const { data } = await axios.get(`${window.env.API_URL}/competition-v2/admin?${params.toString()}`);
    setV2Competitions(data?.data || []);
    return data;
  };

  const fetchSelectedCompetitionDetail = async (competitionId) => {
    if (!competitionId) {
      setSelectedCompetitionDetail(null);
      return null;
    }
    const { data } = await axios.get(`${window.env.API_URL}/competition-v2/${competitionId}/admin`);
    setSelectedCompetitionDetail(data?.data || null);
    return data?.data || null;
  };

  const loadDropdowns = () => {
    fetchCompetitions(competitionSearch).catch(() => {});
    fetchSelectedCompetitionDetail(selectedCompetitionId).catch(() => {});
  };

  useEffect(() => {
    fetchCompetitions("").catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch]);

  useEffect(() => {
    if (presetCompetitionId) {
      setSelectedCompetitionId(presetCompetitionId);
    }
  }, [presetCompetitionId]);

  useEffect(() => {
    fetchSelectedCompetitionDetail(selectedCompetitionId).catch(() => {
      setSelectedCompetitionDetail(null);
    });
  }, [selectedCompetitionId]);

  useEffect(() => {
    if (!selectedCompetition?.Status) return;
    const allowed = ALLOWED_STATUS_TRANSITIONS[selectedCompetition.Status] || [
      selectedCompetition.Status,
    ];
    const currentFormStatus = manageForm.getFieldValue("Status");
    if (!currentFormStatus || !allowed.includes(currentFormStatus)) {
      manageForm.setFieldsValue({ Status: allowed[0] });
    }
  }, [selectedCompetition?.Status, manageForm]);

  const runAction = async (action) => {
    setLoading(true);
    try {
      await action();
      swal("Success!", "Action completed successfully", "success");
    } catch (error) {
      const message = error?.response?.data?.message || error.message || "Request failed";
      swal("Error!", message, "error");
    } finally {
      setLoading(false);
    }
  };

  const onUpdateCompetition = (values) =>
    runAction(async () => {
      if (!selectedCompetitionId) {
        throw new Error("Please select a Competition");
      }
      const body = {
        Status: values.Status,
      };
      if (values.AllowedHorseSelections !== undefined && values.AllowedHorseSelections !== null && values.AllowedHorseSelections !== "") {
        body.AllowedHorseSelections = Number(values.AllowedHorseSelections);
      }
      const { data } = await axios.put(
        `${window.env.API_URL}/competition-v2/${selectedCompetitionId}`,
        body
      );
      setSelectedCompetitionDetail((prev) => ({ ...(prev || {}), ...(data?.data || {}) }));
      setV2Competitions((prev) =>
        prev.map((item) => (item._id === selectedCompetitionId ? { ...item, ...(data?.data || {}) } : item))
      );
      return data;
    });

  const onRunLifecycle = () =>
    runAction(async () => {
      const { data } = await axios.post(`${window.env.API_URL}/competition-v2/lifecycle/run`);
      await fetchCompetitions(competitionSearch).catch(() => {});
      await fetchSelectedCompetitionDetail(selectedCompetitionId).catch(() => {});
      return data;
    });

  const onGetLeaderboard = () =>
    runAction(async () => {
      if (!selectedCompetitionId) {
        throw new Error("Please select a Competition");
      }
      const { data } = await axios.get(
        `${window.env.API_URL}/competition-v2/${selectedCompetitionId}/leaderboard`
      );
      return data;
    });

  const openLeaderboardModal = async () => {
    if (!selectedCompetitionId) {
      swal("Error!", "Please select a Competition", "error");
      return;
    }
    setShowLeaderboardModal(true);
    setLeaderboardLoading(true);
    try {
      const { data } = await axios.get(
        `${window.env.API_URL}/competition-v2/${selectedCompetitionId}/leaderboard`
      );
      setLeaderboardRows(data?.data || []);
    } catch (error) {
      const message = error?.response?.data?.message || error.message || "Request failed";
      swal("Error!", message, "error");
      setShowLeaderboardModal(false);
    } finally {
      setLeaderboardLoading(false);
    }
  };

  const openWinnersModal = async () => {
    if (!selectedCompetitionId) {
      swal("Error!", "Please select a Competition", "error");
      return;
    }
    setShowWinnersModal(true);
    setWinnersLoading(true);
    try {
      const [winnersResponse, leaderboardResponse] = await Promise.all([
        axios.get(`${window.env.API_URL}/competition-v2/${selectedCompetitionId}/winners`),
        axios.get(`${window.env.API_URL}/competition-v2/${selectedCompetitionId}/leaderboard`),
      ]);
      setWinnersRows(winnersResponse?.data?.data || []);
      setWinnersPublished(Boolean(winnersResponse?.data?.isPublished));
      setLeaderboardRows(leaderboardResponse?.data?.data || []);
    } catch (error) {
      const message = error?.response?.data?.message || error.message || "Request failed";
      swal("Error!", message, "error");
      setShowWinnersModal(false);
    } finally {
      setWinnersLoading(false);
    }
  };

  const onAddWinner = () =>
    runAction(async () => {
      if (!selectedCompetitionId) {
        throw new Error("Please select a Competition");
      }
      if (!addWinnerSubscriberId) {
        throw new Error("Please select a subscriber to add");
      }
      const { data } = await axios.post(`${window.env.API_URL}/competition-v2/${selectedCompetitionId}/winners`, {
        SubscriberID: addWinnerSubscriberId,
      });
      setWinnersRows(data?.data || []);
      setWinnersPublished(Boolean(data?.isPublished));
      setAddWinnerSubscriberId("");
      return data;
    });

  const onDeleteWinner = (winnerId) => {
    if (!winnerId) return;
    if (winnersPublished) return;
    swal({
      title: "Are you sure?",
      text: "Do you want to remove this winner?",
      icon: "warning",
      buttons: true,
      dangerMode: true,
    }).then((confirmed) => {
      if (!confirmed) return;
      runAction(async () => {
        const { data } = await axios.delete(
          `${window.env.API_URL}/competition-v2/${selectedCompetitionId}/winners/${winnerId}`
        );
        setWinnersRows(data?.data || []);
        setWinnersPublished(Boolean(data?.isPublished));
        return data;
      });
    });
  };

  const onEditWinnerPrize = (winnerId, prizeAmount) => {
    if (winnersPublished) return;
    setWinnersRows((prev) =>
      prev.map((row) =>
        row._id === winnerId
          ? {
              ...row,
              PrizeAmount: prizeAmount,
            }
          : row
      )
    );
  };

  const onPublishWinners = () =>
    runAction(async () => {
      if (!selectedCompetitionId) {
        throw new Error("Please select a Competition");
      }
      if (winnersPublished) {
        throw new Error("Winners already published");
      }
      if (!winnersRows.length) {
        throw new Error("No winners found to publish");
      }
      const payload = winnersRows.map((row) => ({
        _id: row._id,
        PrizeAmount: Number(row.PrizeAmount || 0),
      }));
      const { data } = await axios.post(
        `${window.env.API_URL}/competition-v2/${selectedCompetitionId}/winners/publish`,
        { Winners: payload }
      );
      setWinnersRows(data?.data || []);
      setWinnersPublished(Boolean(data?.isPublished));
      setSelectedCompetitionDetail((prev) => ({
        ...(prev || {}),
        Status: "finalized",
        WinnersPublishedAtUtc: new Date().toISOString(),
      }));
      setV2Competitions((prev) =>
        prev.map((item) =>
          item._id === selectedCompetitionId
            ? { ...item, Status: "finalized", WinnersPublishedAtUtc: new Date().toISOString() }
            : item
        )
      );
      return data;
    });

  const onSaveWinners = () =>
    runAction(async () => {
      if (!selectedCompetitionId) {
        throw new Error("Please select a Competition");
      }
      if (winnersPublished) {
        throw new Error("Winners already published");
      }
      if (!winnersRows.length) {
        throw new Error("No winners found to save");
      }
      const payload = winnersRows.map((row) => ({
        _id: row._id,
        PrizeAmount: Number(row.PrizeAmount || 0),
      }));
      const { data } = await axios.put(
        `${window.env.API_URL}/competition-v2/${selectedCompetitionId}/winners`,
        { Winners: payload }
      );
      setWinnersRows(data?.data || []);
      setWinnersPublished(Boolean(data?.isPublished));
      return data;
    });

  const onFinalize = (values) =>
    runAction(async () => {
      if (!selectedCompetitionId) {
        throw new Error("Please select a Competition");
      }
      const { data } = await axios.post(
        `${window.env.API_URL}/competition-v2/${selectedCompetitionId}/finalize`,
        {
          WinnerCount: Number(values.WinnerCount),
          WinnerPrizeAmount: Number(values.WinnerPrizeAmount),
        }
      );
      setShowFinalizeModal(false);
      setWinnersRows(data?.data || []);
      setWinnersPublished(Boolean(data?.isPublished));
      setShowWinnersModal(true);
      return data;
    });

  return (
    <div className="page">
      <div className="rightsidedata">
        <div style={{ marginTop: "30px" }}>
          <div className="Header ">
            <h4>Competition Manage</h4>
            <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
              <button
                className="SubmitButton"
                type="button"
                onClick={openLeaderboardModal}
                disabled={loading || !selectedCompetitionId}
              >
                Leaderboard
              </button>
              <button
                className="SubmitButton"
                type="button"
                onClick={openWinnersModal}
                disabled={loading || !selectedCompetitionId}
              >
                Winners
              </button>
              {!hideFinalizeButton ? (
                <button
                  className="SubmitButton"
                  type="button"
                  onClick={() => {
                    if (!selectedCompetitionId) {
                      swal("Error!", "Please select a Competition", "error");
                      return;
                    }
                    setShowFinalizeModal(true);
                  }}
                  disabled={loading || !selectedCompetitionId}
                >
                  Finalize
                </button>
              ) : null}
              <OverlayTrigger overlay={<Tooltip id="tooltip-refresh-v2">Fetch New</Tooltip>}>
                <span className="addmore" onClick={loadDropdowns}>
                  <AiOutlineReload />
                </span>
              </OverlayTrigger>
            </div>
          </div>

          <div className="form" style={{ marginTop: "20px" }}>
            <div className="row mainrow">
              <div className="col-sm">
                <Form.Item label="Competition" style={{ marginBottom: 0 }}>
                  <Select
                    value={selectedCompetitionId || undefined}
                    onChange={(value) => {
                      setSelectedCompetitionId(value);
                    }}
                    placeholder="Select Competition"
                    showSearch
                    filterOption={false}
                    onSearch={(value) => {
                      setCompetitionSearch(value);
                      fetchCompetitions(value).catch(() => {});
                    }}
                    onFocus={() => {
                      fetchCompetitions(competitionSearch).catch(() => {});
                    }}
                  >
                    {competitionOptions.map((item) => (
                      <Option key={item.value} value={item.value}>
                        {item.label}
                      </Option>
                    ))}
                  </Select>
                </Form.Item>
              </div>
              <div className="col-sm">
                <p style={{ marginTop: "14px" }}>
                  Current Status: <b>{selectedCompetition?.Status || "N/A"}</b>
                </p>
              </div>
            </div>
          </div>

          <div className="form" style={{ marginTop: "20px" }}>
            <Form form={manageForm} onFinish={onUpdateCompetition} name="competition_v2_update">
              <div className="row mainrow">
                <div className="col-sm">
                  <Form.Item
                    name="Status"
                    label="Status"
                    rules={[{ required: true, message: "Status is required" }]}
                  >
                    <Select placeholder="Select status">
                      {availableStatusOptions.map((item) => (
                        <Option key={item.value} value={item.value}>
                          {item.label}
                        </Option>
                      ))}
                    </Select>
                  </Form.Item>
                </div>
                <div className="col-sm">
                  <Form.Item name="AllowedHorseSelections" label="Allowed Horse Selections (optional)">
                    <Input type="number" min="1" />
                  </Form.Item>
                </div>
              </div>
              <div className="ButtonSection" style={{ justifyContent: "end" }}>
                <button className="SubmitButton" type="submit" disabled={loading || !selectedCompetitionId}>
                  Update Competition
                </button>
              </div>
            </Form>
          </div>

        </div>
      </div>
      <Modal
        show={showLeaderboardModal}
        onHide={() => setShowLeaderboardModal(false)}
        size="lg"
        aria-labelledby="competition-v2-leaderboard-modal"
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title id="competition-v2-leaderboard-modal">
            Competition Leaderboard
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div
            className="div_maintb"
            style={{ maxHeight: "60vh", overflowY: "auto", overflowX: "auto" }}
          >
            <table style={{ width: "95%", minWidth: "0", tableLayout: "fixed" }}>
              <thead>
                <tr>
                  <th style={{ width: "80px" }}>Rank</th>
                  <th style={{ width: "220px" }}>Subscriber</th>
                  <th style={{ width: "120px" }}>Points</th>
                </tr>
              </thead>
              <tbody>
                {leaderboardLoading ? (
                  <tr>
                    <td colSpan={3}>Loading leaderboard...</td>
                  </tr>
                ) : leaderboardRows.length === 0 ? (
                  <tr>
                    <td colSpan={3}>No leaderboard entries found.</td>
                  </tr>
                ) : (
                  leaderboardRows.map((row, index) => (
                    <tr key={row._id || `${row.SubscriberID}-${index}`}>
                      <td>{index + 1}</td>
                      <td
                        style={{
                          maxWidth: "220px",
                          whiteSpace: "nowrap",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                        }}
                        title={`${row?.CompetitionLeaderboardSubscriberDataV2?.FirstName || ""} ${row?.CompetitionLeaderboardSubscriberDataV2?.LastName || ""}`.trim()}
                      >
                        {row?.CompetitionLeaderboardSubscriberDataV2?.FirstName || ""}{" "}
                        {row?.CompetitionLeaderboardSubscriberDataV2?.LastName || ""}
                      </td>
                      <td>{row.TotalPoint}</td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </Modal.Body>
      </Modal>
      <Modal
        show={showWinnersModal}
        onHide={() => setShowWinnersModal(false)}
        size="lg"
        aria-labelledby="competition-v2-winners-modal"
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title id="competition-v2-winners-modal">Competition Winners</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div
            style={{
              width: "100%",
              display: "flex",
              justifyContent: "flex-end",
              alignItems: "center",
              gap: "8px",
              marginBottom: "10px",
            }}
          >
            <Select
              value={addWinnerSubscriberId || undefined}
              placeholder="Select subscriber to add"
              style={{
                width: "260px",
                border: "1px solid #d9d9d9",
                borderRadius: "6px",
              }}
              showSearch
              optionFilterProp="children"
              disabled={winnersPublished}
              onChange={(value) => setAddWinnerSubscriberId(value)}
            >
              {addWinnerOptions.map((option) => (
                <Option key={option.value} value={option.value}>
                  {option.label}
                </Option>
              ))}
            </Select>
            <button
              className="AddWinnerButton"
              type="button"
              onClick={onAddWinner}
              disabled={loading || winnersLoading || winnersPublished || !addWinnerSubscriberId}
            >
              Add Winner
            </button>
          </div>
          <div
            className="div_maintb"
            style={{ maxHeight: "60vh", overflowY: "auto", overflowX: "auto" }}
          >
            <table style={{ width: "95%", minWidth: "0", tableLayout: "fixed" }}>
              <thead>
                <tr>
                  <th style={{ width: "80px" }}>Rank</th>
                  <th style={{ width: "220px" }}>Subscriber</th>
                  <th style={{ width: "120px" }}>Points</th>
                  <th style={{ width: "180px" }}>Prize</th>

                  {!winnersPublished ? (<th style={{ width: "80px" }}>Action</th>) : null}
                </tr>
              </thead>
              <tbody>
                {winnersLoading ? (
                  <tr>
                    <td colSpan={5}>Loading winners...</td>
                  </tr>
                ) : winnersRows.length === 0 ? (
                  <tr>
                    <td colSpan={5}>No winners found for this competition.</td>
                  </tr>
                ) : (
                  winnersRows.map((row, index) => (
                    <tr key={row._id || `${row.SubscriberID}-${index}`}>
                      <td>{row?.Rank || index + 1}</td>
                      <td
                        style={{
                          maxWidth: "220px",
                          whiteSpace: "nowrap",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                        }}
                        title={`${row?.CompetitionWinnerSubscriberDataV2?.FirstName || ""} ${row?.CompetitionWinnerSubscriberDataV2?.LastName || ""}`.trim()}
                      >
                        {row?.CompetitionWinnerSubscriberDataV2?.FirstName || ""}{" "}
                        {row?.CompetitionWinnerSubscriberDataV2?.LastName || ""}
                      </td>
                      <td>{row?.TotalPoint ?? 0}</td>
                      <td>
                        <Input
                          type="number"
                          min="0"
                          value={row?.PrizeAmount ?? 0}
                          disabled={winnersPublished}
                          style={{ minWidth: "120px" }}
                          onChange={(e) => onEditWinnerPrize(row._id, e.target.value)}
                        />
                      </td>
                      {!winnersPublished ? (
                      <td>
                        <button className="DeleteWinnerButton" onClick={() => onDeleteWinner(row._id)}>
                          Remove
                        </button>
                      </td>) : null}
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </Modal.Body>
        <Modal.Footer>
          <div
            style={{
              width: "100%",
              display: "flex",
              justifyContent: "flex-end",
              alignItems: "center",
              marginBottom: "8px",
            }}
          >
            <span style={{ fontWeight: 600, fontSize: "20px" }}>
              Total Prize: {winnersPrizeTotal}
            </span>
          </div>
          <div
            style={{
              width: "100%",
              display: "flex",
              justifyContent: "flex-end",
              alignItems: "center",
              gap: "8px",
              paddingTop: "8px",
            }}
          >
            
            <button
              className="SubmitButton"
              type="button"
              onClick={onPublishWinners}
              disabled={loading || winnersLoading || winnersPublished || winnersRows.length === 0}
            >
              Publish Winners
            </button>
            <button
              className="SubmitButton"
              type="button"
              onClick={onSaveWinners}
              disabled={loading || winnersLoading || winnersPublished || winnersRows.length === 0}
            >
              Save
            </button>
          </div>
        </Modal.Footer>
      </Modal>
      <Modal
        show={showFinalizeModal}
        onHide={() => setShowFinalizeModal(false)}
        size="md"
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>Finalize Competition</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form form={finalizeForm} onFinish={onFinalize} name="competition_v2_finalize_modal">
            <div className="row mainrow">
              <div className="col-sm">
                <Form.Item
                  name="WinnerCount"
                  label="Winner Count"
                  initialValue={3}
                  rules={[{ required: true, message: "Winner count is required" }]}
                >
                  <Input type="number" min="1" />
                </Form.Item>
              </div>
            </div>
            <div className="row mainrow">
              <div className="col-sm">
                <Form.Item
                  name="WinnerPrizeAmount"
                  label="Winner Prize Amount"
                  initialValue={0}
                  rules={[{ required: true, message: "Winner prize amount is required" }]}
                >
                  <Input type="number" min="0" />
                </Form.Item>
              </div>
            </div>
            <div className="ButtonSection" style={{ justifyContent: "end" }}>
              <button
                className="SubmitButton"
                type="button"
                onClick={() => setShowFinalizeModal(false)}
                style={{ marginRight: "8px" }}
              >
                Cancel
              </button>
              <button className="SubmitButton" type="submit" disabled={loading}>
                Finalize
              </button>
            </div>
          </Form>
        </Modal.Body>
      </Modal>
    </div>
  );
};

export default CompetitionV2Admin;
